/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { UtilsService } from '../../../../../core/services';
import { Injectable } from '@angular/core';

@Injectable()
export class UserRoutingService {
    /**
     * To store the routes.
     * @private
     */
    private _routes;

    constructor(
        private utilsService: UtilsService
    ) {
    }

    /**
     * To determine weather the admin access is activated or not.
     */
    public adminAccess: boolean = false;

    /**
     * Get the routes.
     */
    get routes() {
        return this._routes;
    }

    /**
     * Set the routes.
     * @param routes
     */
    set routes(routes) {
        this._routes = routes;
    }

    /**
     * Split the supplied path into individual array elements for processing.
     * @param path
     */
    public splitPath(path): string[] {
        if (path === '/') {
            return [path];
        } else {
            const splitPath = path.split('/');

            return this.utilsService.cleanArray(splitPath);
        }
    }

    /**
     * Toggle the admin access.
     * @param accessOptions
     */
    public toggleAdminAccess(accessOptions): void {
        for (const access of accessOptions) {
            this.toggleAccess('/', access, !this.adminAccess, true);
        }
    }

    /**
     * Toggle access parent container.
     * @param path
     * @param permission
     * @param removePermission
     * @param adminAccess
     */
    public toggleAccess(path, permission, removePermission, adminAccess): void {
        this.setTargetPathAccess(path, permission, removePermission);
        const splitPath = this.splitPath(path);

        if (splitPath[0] !== '/') {
            this.setParentRoutesPermissions(splitPath, permission);
        }

        if (splitPath[0] === '/') {
            for (const route of this.routes[0].children) {
                if (route.collapsed || route.access.length === 0) {
                    this.toggleCollapse(route);
                }
            }
        }

        if (!adminAccess) {
            this.adminAccess = this.routes[0].access.length === 4;
        }
    }

    /**
     * Set the parent route's permissions starting from the top.
     * @param splitPath
     * @param permission
     * @private
     */
    private setParentRoutesPermissions(splitPath, permission): void {
        if (splitPath.length === 1) {
            this.checkChildrenForAccess(this.routes[0], permission);
        }
        const target = splitPath.pop();
        const parent = splitPath[splitPath.length - 1];
        this.findParentOfTarget(target, parent, this.routes[0], splitPath, permission);
    }

    /**
     * Find the parent of the target path in order to differentiate between routes
     * with the same name.
     * @param target
     * @param parent
     * @param routes
     * @param splitPath
     * @param permission
     * @private
     */
    private findParentOfTarget(target, parent, routes, splitPath, permission): void {
        for (const route of routes.children) {
            const foundTargetChild = route.children.filter(item => item.path === target);
            if (foundTargetChild.length && route.path === parent) {
                this.checkChildrenForAccess(route, permission);
                this.setParentRoutesPermissions(splitPath, permission);
            } else {
                this.findParentOfTarget(target, parent, route, splitPath, permission);
            }
        }
    }

    /**
     * Set the target path's access as well as its children.
     * @param path
     * @param permission
     * @param removePermission
     * @private
     */
    private setTargetPathAccess(path, permission, removePermission): void {
        const splitPath = this.splitPath(path);
        if (path === '/') {
            this.setPermissionForAdminRoute(path, removePermission, permission);
        }

        if (splitPath.length === 1) {
            this.setPermissionForRootChild(splitPath, removePermission, permission);
        }

        if (splitPath.length > 1) {
            this.setPermissionForDeepRoutes(splitPath, permission, removePermission);
        }
    }

    /**
     * Remove/set access for admin route.
     * @param path
     * @param removePermission
     * @param permission
     * @private
     */
    private setPermissionForAdminRoute(path, removePermission, permission): void {
        if (!removePermission) {
            this.setTargetPathPermission(this.routes[0], permission);
            this.setTargetPathsChildren(this.routes[0], permission);
        } else {
            this.removeAccess(this.routes[0], permission);
            this.removeAccessFromChildren(this.routes[0], permission);
        }
    }

    /**
     * Remove/ set access for split path.
     * @param splitPath
     * @param removePermission
     * @param permission
     * @private
     */
    private setPermissionForRootChild(splitPath, removePermission, permission): void {
        const lastElement = splitPath.pop();
        for (const route of this.routes[0].children) {
            if (route.path === lastElement) {
                if (!removePermission) {
                    this.setTargetPathPermission(route, permission);
                    this.setTargetPathsChildren(route, permission);
                } else {
                    this.removeAccess(route, permission);
                    this.removeAccessFromChildren(route, permission);
                }
            }
        }
    }

    /**
     * Remove/set permissions for split paths children.
     * @param splitPath
     * @param permission
     * @param removePermission
     * @private
     */
    private setPermissionForDeepRoutes(splitPath, permission, removePermission): void {
        const searchParams = {
            target: splitPath.pop(),
            parent: splitPath[splitPath.length - 1],
            permission: permission,
            removePermission: removePermission
        };

        for (const route of this.routes[0].children) {
            this.findTargetPath(route, searchParams);
        }
    }

    /**
     * Find the target path to enable/disable access.
     * @param route
     * @param searchParams
     * @private
     */
    private findTargetPath(route, searchParams): void {
        if (!route.children.length) {
            return;
        }

        const permission = searchParams.permission;

        for (const child of route.children) {
            if (child.path === searchParams.target && route.path === searchParams.parent) {
                if (!searchParams.removePermission) {
                    this.setTargetPathPermission(child, permission);
                    this.setTargetPathsChildren(child, permission);
                } else {
                    this.removeAccess(child, permission);
                    this.removeAccessFromChildren(child, permission);
                }
            } else {
                this.findTargetPath(child, searchParams);
            }
        }
    }

    /**
     * Set the target path children to have permissions.
     * @param route
     * @param permission
     */
    public setTargetPathsChildren(route, permission): void {
        if (!route.children.length) {
            return;
        }

        for (const child of route.children) {
            this.setTargetPathPermission(child, permission);
            this.setTargetPathsChildren(child, permission);
        }
    }

    /**
     * Set the permissions to the routes.
     * @param route
     * @param permission
     */
    public setTargetPathPermission(route, permission): void {
        if (!route.access.includes(permission)) {
            route.access.push(permission);
        }
    }

    /**
     * Remove the access from the route.
     * @param route
     * @param permission
     * @private
     */
    private removeAccess(route, permission): void {
        const permissionIndex = route.access.indexOf(permission);
        route.access.splice(permissionIndex, 1);
    }

    /**
     * Remove the access from the children.
     * @param route
     * @param permission
     * @private
     */
    private removeAccessFromChildren(route, permission): void {
        if (!route.children.length) {
            return;
        }

        for (const child of route.children) {
            this.removeAccess(child, permission);
            this.removeAccessFromChildren(child, permission);
        }
    }

    /**
     * Check the children for access.
     * @param route
     * @param access
     * @private
     */
    private checkChildrenForAccess(route, access): void {
        let childrenAccessCounter = 0;
        const routeChildren = route.children;
        const routeAccess = route.access;

        for (const child of routeChildren) {
            if (child.access.includes(access)) {
                childrenAccessCounter++;
            }
        }

        if (routeChildren.length === childrenAccessCounter) {
            if (!routeAccess.includes(access)) {
                routeAccess.push(access);
            }
        } else {
            if (routeAccess.includes(access)) {
                const indexOfPermission = routeAccess.indexOf(access);
                routeAccess.splice(indexOfPermission, 1);
            }
        }
    }

    /**
     * Update the resources list based on the routes with access.
     * @param resources
     */
    public updateResourceList(resources): void {
        const rootChildren = this.routes[0].children;
        for (const child of rootChildren) {
            if (child.access.length) {
                const indexOfResource = resources.findIndex(item => item.path === child.fullPath);
                if (indexOfResource === -1) {
                    resources.push({ path: child.fullPath, access: child.access });
                }
            } else {
                const emptyIndex = resources.findIndex(item => item.path === child.fullPath);
                if (emptyIndex !== -1) {
                    resources.splice(emptyIndex, 1);
                }
            }

            if (child.children.length) {
                this.updateChildResource(child, resources);
            }
        }
    }

    /**
     * Update the child resources based on routes with access.
     * @param route
     * @param resources
     * @private
     */
    private updateChildResource(route, resources): void {
        if (!route.children.length) {
            return;
        }

        for (const child of route.children) {
            if (child.access.length) {
                const indexOfResource = resources.findIndex(item => item.path === child.fullPath);
                if (indexOfResource === -1) {
                    resources.push({ path: child.fullPath, access: child.access });
                }
            }

            this.updateChildResource(child, resources);
        }
    }

    /**
     * Create the route with the access set.
     * @param routes
     */
    public createRoutesWithAccess(routes): any {
        if (!routes.length) {
            return;
        }

        routes[0].access = [];

        for (const routeChild of routes[0].children) {
            routeChild.access = [];
            this.addAccessToChild(routeChild);
        }

        return routes;
    }

    /**
     * Add access to the child route.
     * @param routeChild
     * @private
     */
    private addAccessToChild(routeChild): void {
        if (!routeChild.children.length) {
            return;
        }
        routeChild.collapsed = true;
        for (const child of routeChild.children) {
            child.access = [];
            this.addAccessToChild(child);
        }
    }

    /**
     * Toggle to open or close the resource.
     * @param route
     */
    public toggleCollapse(route): void {
        // Set the collapsed value to the opposite of what it currently is.
        route.collapsed = !route.collapsed;
    }
}
